<template>
    <button ref="ButtonElement" v-bind="buttonAttributes" v-on="buttonEvents">
        <Transition name="status-change" mode="out-in">
            <div v-if="isLoading"><Loader />{{loaderText}}</div>
            <div v-else-if="successText && !isLoading && currentStatus === 'success'">{{successText}}</div>
            <div v-else-if="errorText && !isLoading && currentStatus === 'error'">{{errorText}}</div>
            <div v-else><slot></slot></div>
        </Transition>
    </button>
</template>

<script setup>
import { ref, computed, useAttrs, onBeforeUnmount, onMounted, watch } from "vue";

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const props = defineProps(
{
    loaderText     : { type: [ String, Boolean ], default: 'Loading' },
    successText    : { type: [ String, Boolean ], default: 'Success' },
    errorText      : { type: [ String, Boolean ], default: 'Error' },
    handleCallback : { type: Boolean, default: true }
})

const emit = defineEmits([ 'on:click', 'on:focus', 'on:blur' ]);

const ButtonElement        = ref( null );
const isLoading            = ref( !!attrs?.class?.includes('loading') );
const shouldHandleCallback = ref( props.handleCallback );
const currentStatus        = ref( undefined );

const buttonAttributes = computed( () =>
{
    function filterDataAttributes( obj )
    {
        const filteredObject = {};

        for( const key in obj )
        {
            if( key.startsWith('type') || key.startsWith('disabled') || key.startsWith('style') || key.startsWith('data-') )
            {
                filteredObject[key] = obj[key];
            }
        }

        return filteredObject;
    }

    return (
    {
        "class":
        {
            'Button'         : true,
            'loading'        : isLoading.value,
            'success pulse'  : currentStatus.value === 'success',
            'error shake'    : currentStatus.value === 'error',
            [ attrs?.class ] : attrs?.class
        },
        ...filterDataAttributes( attrs )
    })
});

const buttonEvents = computed( () =>
{
    return (
    {
        'focus' : ( event ) => resolveFocus( event, attrs?.onFocus ),
        'click' : ( event ) => resolveClick( event, attrs?.onClick ),
        'blur'  : ( event ) => resolveBlur( event, attrs?.onBlur )
    })
});

const resolveFocus = async ( event, callback ) =>
{
    if( typeof callback === 'function' )
    {
        callback( event );
    }

    emit('on:focus');
}

const resolveClick = async ( event, callback ) =>
{
    if( typeof callback === 'function' )
    {
        let response = callback( event );

        if( response?.constructor?.name === 'Promise' )
        {
            isLoading.value = true;

            ButtonElement?.value?.focus();

            try
            {
                await response;

                isLoading.value = false;

                shouldHandleCallback.value && handleCallback( 'success' );
            }
            catch( error )
            {
                isLoading.value = false;

                shouldHandleCallback.value && handleCallback( 'error' );
            }
        }
    }

    emit('on:click');
}

const resolveBlur = async ( event, callback ) =>
{
    if( typeof callback === 'function' )
    {
        callback( event );
    }

    emit('on:blur');
}

const setEventListeners = () =>
{
    ButtonElement?.value?.addEventListener( 'blur', clearStatusHandle );
    ButtonElement?.value?.addEventListener( 'click', clearStatusHandle );
}

const unsetEventListeners = () =>
{
    ButtonElement?.value?.removeEventListener( 'blur', clearStatusHandle );
    ButtonElement?.value?.removeEventListener( 'click', clearStatusHandle );
}

const handleCallback = ( status ) =>
{
    unsetEventListeners();

    currentStatus.value = status === 'success' ? 'success' : 'error';

    ButtonElement?.value?.addEventListener( 'animationend', () => setTimeout( () => clearStatusHandle(), 1000 ), { once: true } );

    setEventListeners();
}

const clearStatusHandle = () =>
{
    currentStatus.value = undefined;

    ButtonElement?.value?.blur();
};

const load = ( status, callback ) =>
{
    if( status === 'start' ){ isLoading.value = true; }
    if( status === 'end' )  { isLoading.value = false; }

    if( status === 'success' ) { isLoading.value = false; handleCallback( 'success' ) }
    if( status === 'error' )   { isLoading.value = false; handleCallback( 'error' ) }

    //TODO TO MA BYT ASI INDE
    if( callback && typeof callback === 'function' )
    {
        ButtonElement?.value?.addEventListener( 'animationend', () => { callback(); clearStatusHandle(); }, { once: true } );
    }
}

watch( () => props.handleCallback, ( value ) => shouldHandleCallback.value = value );

onMounted(       () => setEventListeners() );
onBeforeUnmount( () => unsetEventListeners() );

defineExpose({ element: ButtonElement, handleCallback: shouldHandleCallback, load });
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Button.primary
        {
            --button-text-color          : var(--dark-text-color-primary);
            --button-hover-text-color    : var(--dark-text-color-primary);
            --button-active-text-color   : var(--dark-text-color-primary);
            --button-focus-text-color    : var(--dark-text-color-primary);
            --button-disabled-text-color : var(--dark-text-color-primary);

            --button-success-text-color  : var(--dark-text-color-primary);
            --button-warning-text-color  : var(--dark-text-color-primary);
            --button-error-text-color    : var(--dark-text-color-primary);
            --button-grayscale-text-color: var(--dark-text-color-primary);

            --button-background-color          : var(--primary-colors-blue-60);
            --button-hover-background-color    : var(--primary-colors-blue-50);
            --button-active-background-color   : var(--primary-colors-blue-70);
            --button-focus-background-color    : var(--primary-colors-blue-60);
            --button-disabled-background-color : var(--primary-colors-blue-30);

            --button-success-background-color   : var(--success-colors-success-40);
            --button-warning-background-color   : var(--warning-colors-warning-40);
            --button-error-background-color     : var(--error-colors-error-40);
            --button-grayscale-background-color : var(--primary-text-color);

            --button-border-color          : transparent;
            --button-hover-border-color    : transparent;
            --button-active-border-color   : transparent;
            --button-focus-border-color    : var(--primary-colors-blue-20);
            --button-disabled-border-color : transparent;

            --button-success-border-color  : transparent;
            --button-warning-border-color  : transparent;
            --button-error-border-color    : transparent;
            --button-grayscale-border-color: transparent;

            --button-border-style           : solid;
            --button-hover-border-style     : solid;
            --button-active-border-style    : solid;
            --button-focus-border-style     : dashed;
            --button-disabled-border-style  : solid;

            --button-success-border-style   : solid;
            --button-warning-border-style   : solid;
            --button-error-border-style     : solid;
            --button-grayscale-border-style : solid;

            --button-border-width           : 1.5px;
            --button-hover-border-width     : 1.5px;
            --button-active-border-width    : 1.5px;
            --button-focus-border-width     : 1.5px;
            --button-disabled-border-width  : 1.5px;

            --button-success-border-width   : 1.5px;
            --button-warning-border-width   : 1.5px;
            --button-error-border-width     : 1.5px;
            --button-grayscale-border-width : 1.5px;
        }

        .Button.secondary
        {
            --button-text-color          : var(--primary-colors-blue-60);
            --button-hover-text-color    : var(--primary-colors-blue-60);
            --button-active-text-color   : var(--primary-colors-blue-70);
            --button-focus-text-color    : var(--primary-colors-blue-60);
            --button-disabled-text-color : var(--light-surfaces-silver);

            --button-success-text-color  : var(--success-colors-success-40);
            --button-warning-text-color  : var(--warning-colors-warning-40);
            --button-error-text-color    : var(--error-colors-error-40);
            --button-grayscale-text-color: var(--primary-text-color);

            --button-background-color          : var(--light-surfaces-white);
            --button-hover-background-color    : var(--primary-colors-blue-10);
            --button-active-background-color   : var(--light-surfaces-white);
            --button-focus-background-color    : var(--light-surfaces-white);
            --button-disabled-background-color : var(--light-surfaces-white);

            --button-success-background-color  : var(--light-surfaces-white);
            --button-warning-background-color  : var(--light-surfaces-white);
            --button-error-background-color    : var(--light-surfaces-white);
            --button-grayscale-background-color: var(--light-surfaces-white);

            --button-border-color          : var(--primary-colors-blue-60);
            --button-hover-border-color    : transparent;
            --button-active-border-color   : var(--primary-colors-blue-70);
            --button-focus-border-color    : var(--primary-colors-blue-60);
            --button-disabled-border-color : var(--light-surfaces-off-white);

            --button-success-border-color  : var(--success-colors-success-40);
            --button-warning-border-color  : var(--warning-colors-warning-40);
            --button-error-border-color    : var(--error-colors-error-40);
            --button-grayscale-border-color: var(--primary-text-color);

            --button-border-style           : solid;
            --button-hover-border-style     : solid;
            --button-active-border-style    : solid;
            --button-focus-border-style     : dashed;
            --button-disabled-border-style  : solid;

            --button-success-border-style   : solid;
            --button-warning-border-style   : solid;
            --button-error-border-style     : solid;
            --button-grayscale-border-style : solid;

            --button-border-width           : 1.5px;
            --button-hover-border-width     : 1.5px;
            --button-active-border-width    : 1.5px;
            --button-focus-border-width     : 1.5px;
            --button-disabled-border-width  : 1.5px;

            --button-success-border-width   : 1.5px;
            --button-warning-border-width   : 1.5px;
            --button-error-border-width     : 1.5px;
            --button-grayscale-border-width : 1.5px;
        }
    }

    &[data-theme="dark"]
    {
        .Button.primary
        {
            --button-text-color          : var(--light-text-color-primary);
            --button-hover-text-color    : var(--light-text-color-primary);
            --button-active-text-color   : var(--light-text-color-primary);
            --button-focus-text-color    : var(--light-text-color-primary);
            --button-disabled-text-color : var(--light-text-color-disabled);

            --button-success-text-color  : var(--light-text-color-primary);
            --button-warning-text-color  : var(--light-text-color-primary);
            --button-error-text-color    : var(--light-text-color-primary);
            --button-grayscale-text-color: var(--light-text-color-primary);

            --button-background-color          : var(--primary-colors-blue-40);
            --button-hover-background-color    : var(--primary-colors-blue-50);
            --button-active-background-color   : var(--primary-colors-blue-30);
            --button-focus-background-color    : var(--primary-colors-blue-30);
            --button-disabled-background-color : var(--dark-surfaces-gunmetal);

            --button-success-background-color  : var(--success-colors-success-40);
            --button-warning-background-color  : var(--warning-colors-warning-40);
            --button-error-background-color    : var(--error-colors-error-40);
            --button-grayscale-background-color: var(--light-surfaces-bright-grey);

            --button-border-color          : transparent;
            --button-hover-border-color    : transparent;
            --button-active-border-color   : transparent;
            --button-focus-border-color    : var(--primary-colors-blue-50);
            --button-disabled-border-color : transparent;

            --button-success-border-color  : transparent;
            --button-warning-border-color  : transparent;
            --button-error-border-color    : transparent;
            --button-grayscale-border-color: transparent;

            --button-border-style           : solid;
            --button-hover-border-style     : solid;
            --button-active-border-style    : solid;
            --button-focus-border-style     : dashed;
            --button-disabled-border-style  : solid;

            --button-success-border-style   : solid;
            --button-warning-border-style   : solid;
            --button-error-border-style     : solid;
            --button-grayscale-border-style : solid;

            --button-border-width           : 1.5px;
            --button-hover-border-width     : 1.5px;
            --button-active-border-width    : 1.5px;
            --button-focus-border-width     : 1.5px;
            --button-disabled-border-width  : 1.5px;

            --button-success-border-width   : 1.5px;
            --button-warning-border-width   : 1.5px;
            --button-error-border-width     : 1.5px;
            --button-grayscale-border-width : 1.5px;
        }

        .Button.secondary
        {
            --button-text-color          : var(--primary-colors-blue-40);
            --button-hover-text-color    : var(--primary-colors-blue-50);
            --button-active-text-color   : var(--primary-colors-blue-30);
            --button-focus-text-color    : var(--primary-colors-blue-30);
            --button-disabled-text-color : var(--dark-surfaces-slate);

            --button-success-text-color  : var(--success-colors-success-40);
            --button-warning-text-color  : var(--warning-colors-warning-40);
            --button-error-text-color    : var(--error-colors-error-40);
            --button-grayscale-text-color: var(--primary-text-color);

            --button-background-color          : transparent;
            --button-hover-background-color    : transparent;
            --button-active-background-color   : transparent;
            --button-focus-background-color    : transparent;
            --button-disabled-background-color : transparent;

            --button-success-background-color  : transparent;
            --button-warning-background-color  : transparent;
            --button-error-background-color    : transparent;
            --button-grayscale-background-color: transparent;

            --button-border-color          : var(--primary-colors-blue-40);
            --button-hover-border-color    : var(--primary-colors-blue-50);
            --button-active-border-color   : var(--primary-colors-blue-30);
            --button-focus-border-color    : var(--primary-colors-blue-30);
            --button-disabled-border-color : var(--dark-surfaces-slate);

            --button-success-border-color  : var(--success-colors-success-40);
            --button-warning-border-color  : var(--warning-colors-warning-40);
            --button-error-border-color    : var(--error-colors-error-40);
            --button-grayscale-border-color: var(--primary-text-color);

            --button-border-style           : solid;
            --button-hover-border-style     : solid;
            --button-active-border-style    : solid;
            --button-focus-border-style     : dashed;
            --button-disabled-border-style  : solid;

            --button-success-border-style   : solid;
            --button-warning-border-style   : solid;
            --button-error-border-style     : solid;
            --button-grayscale-border-style : solid;

            --button-border-width           : 1.5px;
            --button-hover-border-width     : 1.5px;
            --button-active-border-width    : 1.5px;
            --button-focus-border-width     : 1.5px;
            --button-disabled-border-width  : 1.5px;

            --button-success-border-width   : 1.5px;
            --button-warning-border-width   : 1.5px;
            --button-error-border-width     : 1.5px;
            --button-grayscale-border-width : 1.5px;
        }
    }
}

.Button
{
    display: flex;
    align-items: center;
    justify-content: center;

    flex-shrink: 0;

    color: var(--button-text-color);
    background-color: var(--button-background-color);

    border-style: var(--button-border-style);
    border-width: var(--button-border-width);
    border-color: var(--button-border-color);
    border-radius: 4px;

    user-select: none;
    cursor: pointer;

    transition: all 250ms ease-in-out;

    &.xl
    {
        height: px(56);
        padding: px(16) px(24);

        font-size: fpx(16);
        line-height: fpx(24);
        font-weight: 500;
        font-style: normal;

        i
        {
            display: flex;
            align-items: center;
            justify-content: center;

            width: px(24);
            height: px(24);
            font-size: px(24);
        }

        &.icon
        {
            width: px(56);
            padding: px(16);
        }

        > div
        {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            gap: px(8);
        }
    }

    &.lg
    {
        height: px(48);
        padding: px(12) px(24);

        font-size: fpx(16);
        line-height: fpx(24);
        font-weight: 500;
        font-style: normal;

        i
        {
            display: flex;
            align-items: center;
            justify-content: center;

            width: px(24);
            height: px(24);
            font-size: px(24);
        }

        &.icon
        {
            width: px(48);
            padding: px(12);
        }

        > div
        {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            gap: px(8);
        }
    }

    &.md
    {
        height: px(40);
        padding: px(8) px(24);

        font-size: fpx(14);
        line-height: fpx(20);
        font-weight: 500;
        font-style: normal;

        i
        {
            display: flex;
            align-items: center;
            justify-content: center;

            width: px(20);
            height: px(20);
            font-size: px(20);
        }

        &.icon
        {
            width: px(40);
            padding: px(8);
        }

        > div
        {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            gap: px(8);
        }
    }

    &.sm
    {
        height: px(36);
        padding: px(8) px(24);

        font-size: fpx(14);
        line-height: fpx(20);
        font-weight: 500;
        font-style: normal;

        i
        {
            display: flex;
            align-items: center;
            justify-content: center;

            width: px(20);
            height: px(20);
            font-size: px(20);
        }

        &.icon
        {
            width: px(36);
            padding: px(8);
        }

        > div
        {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            gap: px(8);
        }
    }

    &.hovered,
    &:hover
    {
        color: var(--button-hover-text-color);
        background-color: var(--button-hover-background-color);

        border-style: var(--button-hover-border-style);
        border-width: var(--button-hover-border-width);
        border-color: var(--button-hover-border-color);
    }

    &.actived,
    &:active
    {
        color: var(--button-active-text-color);
        background-color: var(--button-active-background-color);

        border-style: var(--button-active-border-style);
        border-width: var(--button-active-border-width);
        border-color: var(--button-active-border-color);
    }

    &.focused,
    &:focus
    {
        color: var(--button-focus-text-color);
        background-color: var(--button-focus-background-color);

        border-style: var(--button-focus-border-style);
        border-width: var(--button-focus-border-width);
        border-color: var(--button-focus-border-color);
    }

    &.disabled,
    &:disabled
    {
        color: var(--button-disabled-text-color);
        background-color: var(--button-disabled-background-color);

        border-style: var(--button-disabled-border-style);
        border-width: var(--button-disabled-border-width);
        border-color: var(--button-disabled-border-color);
    }


    &.loading
    {
        pointer-events: none;
    }

    &.success
    {
        color: var(--button-success-text-color);
        background-color: var(--button-success-background-color);

        border-style: var(--button-success-border-style);
        border-width: var(--button-success-border-width);
        border-color: var(--button-success-border-color);
    }

    &.warning
    {
        color: var(--button-warning-text-color);
        background-color: var(--button-warning-background-color);

        border-style: var(--button-warning-border-style);
        border-width: var(--button-warning-border-width);
        border-color: var(--button-warning-border-color);
    }

    &.error
    {
        color: var(--button-error-text-color);
        background-color: var(--button-error-background-color);

        border-style: var(--button-error-border-style);
        border-width: var(--button-error-border-width);
        border-color: var(--button-error-border-color);
    }

    &.grayscale
    {
        color: var(--button-grayscale-text-color);
        background-color: var(--button-grayscale-background-color);

        border-style: var(--button-grayscale-border-style);
        border-width: var(--button-grayscale-border-width);
        border-color: var(--button-grayscale-border-color);
    }


    @keyframes pulse
    {
        0%   { box-shadow: 0 0 0 0 rgba(var(--button-bg), 0.4); }
        70%  { box-shadow: 0 0 0 #{px(8)} rgba(var(--button-bg), 0); }
        100% { box-shadow: 0 0 0 0 rgba(var(--button-bg), 0); }
    }

    @keyframes shake
    {
        10%, 90%      { transform: translate3d(#{px(-1)}, 0, 0); }
        20%, 80%      { transform: translate3d(#{px(2)}, 0, 0); }
        30%, 50%, 70% { transform: translate3d(#{px(-2)}, 0, 0); }
        40%, 60%      { transform: translate3d(#{px(2)}, 0, 0); }
    }

    &.pulse
    {
        animation: pulse 0;
        animation-delay: 550ms;
        pointer-events: none;
    }

    &.shake
    {
        animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
        animation-delay: 550ms;
        pointer-events: none;
    }

    > .status-change-enter-active { transition: opacity 250ms ease-in, transform 250ms ease-in; }
    > .status-change-leave-active { transition: opacity 250ms ease-out, transform 250ms ease-out; }
    > .status-change-enter-from   { opacity: 0; transform: translateX(#{px(12)}); }
    > .status-change-leave-to     { opacity: 0; transform: translateX(#{px(-12)}); }
}
</style>